import type { TimesheetEntry } from '@/types/timesheet';
import type { AbsenceEntryDto } from '@/services/absencesApi';
import type { EmployeeRoleInfo } from '@/services/scheduledBookingService';

function getAbsenceDescription(absenceType?: string | null): string {
  switch (absenceType) {
    case 'annual-leave':
      return 'Annual Leave';
    case 'absence':
      return 'Absence';
    case 'other':
      return 'Other Absence';
    case 'maternity':
    case 'maternity-paternity':
      return 'Maternity/Paternity Leave';
    default:
      return 'Absence';
  }
}

/**
 * Converts a backend AbsenceEntryDto into a TimesheetEntry that the grid can render.
 * Uses a synthetic negative ID to avoid collision with real timesheet entry IDs.
 */
export function mapAbsenceToTimesheetEntry(
  dto: AbsenceEntryDto,
  index: number,
  employeeRole?: EmployeeRoleInfo,
): TimesheetEntry {
  return {
    // Use negative IDs starting from -10000 to avoid collisions
    id: -(10_000 + dto.id),
    projectId: dto.defaultProjectId ?? '',
    costCentreId: dto.defaultCostCentreId ?? '',
    roleId: employeeRole?.roleId,
    roleName: employeeRole?.roleId ? employeeRole.roleName : undefined,
    description: getAbsenceDescription(dto.absenceType),
    hours: {
      monday: dto.hoursPerDay?.monday ?? 0,
      tuesday: dto.hoursPerDay?.tuesday ?? 0,
      wednesday: dto.hoursPerDay?.wednesday ?? 0,
      thursday: dto.hoursPerDay?.thursday ?? 0,
      friday: dto.hoursPerDay?.friday ?? 0,
      saturday: dto.hoursPerDay?.saturday ?? 0,
      sunday: dto.hoursPerDay?.sunday ?? 0,
    },
    sourceIntegration: (dto.source as 'people-hr' | 'other') ?? 'people-hr',
    isReadOnly: true,
    isNonProjectTime: true,
    absenceType: dto.absenceType as TimesheetEntry['absenceType'],
    absenceReason: dto.reason ?? undefined,
    absenceId: dto.externalLeaveId,
  };
}
